import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowUpOutlined,
    CheckOutlined,
    DownloadOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FolderOutlined,
    MoreOutlined,
    PlayCircleOutlined,
    PushpinFilled,
} from "@ant-design/icons";
import {
    Button,
    Checkbox,
    Dropdown,
    Empty,
    Modal,
    Spin,
    Typography,
    message,
} from "antd";
import { DownloadButton, ActionLabel } from "./DownloadAction";
import DeliveryFilePreview from "./DeliveryFilePreview";
import DeliveryLazyImage from "./DeliveryLazyImage";
import DeliveryViewToggle from "./DeliveryViewToggle";
import MobileBatchSavePanel from "./MobileBatchSavePanel";
import MobileZipSuggestModal from "./MobileZipSuggestModal";
import ScrollNavigator from "./ScrollNavigator";
import SocialMediaCards from "./SocialMediaCards";
import FeedbackPrompt from "../feedback/FeedbackPrompt";
import { useTranslation } from "../../i18n";
import { useDragSelect } from "../../hooks/useDragSelect";
import { useLongPress } from "../../hooks/useLongPress";
import {
    useIsMobileDeliveryDevice,
} from "../../utils/device";
import {
    buildDownloadManifest,
    downloadDeliveryFile,
    downloadEntriesAsStreamingZip,
    downloadFolderAsZip,
    downloadManifestAsStreamingZip,
} from "../../utils/deliveryDownload";
import {
    getDownloadAllUrl,
    getFileThumbnailUrl,
    isDeliveryApiConfigured,
    isFolderItem,
    isImageItem,
    listDeliveryFolder,
} from "../../utils/deliveryApi";
import { toFolderPrefix } from "../../utils/deliveryPath";
import {
    getDeliveryPreviewKind,
    isDeliveryPreviewable,
} from "../../utils/deliveryFileTypes";

const { Text } = Typography;

function renderFileIcon(item) {
    switch (getDeliveryPreviewKind(item)) {
        case "pdf":
            return <FilePdfOutlined className="delivery-browser__file-icon delivery-browser__file-icon--pdf" />;
        case "video":
            return <PlayCircleOutlined className="delivery-browser__file-icon delivery-browser__file-icon--video" />;
        case "text":
            return <FileTextOutlined className="delivery-browser__file-icon" />;
        default:
            return <FileOutlined className="delivery-browser__file-icon" />;
    }
}

function getFolderPathPrefix(breadcrumbs) {
    if (breadcrumbs.length <= 1) {
        return "";
    }
    return `${breadcrumbs
        .slice(1)
        .map((crumb) => crumb.name)
        .join("/")}/`;
}

function DriveFolderBrowser({ albumPath, albumName, downloadAll, linkId, sourceType = "delivery" }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isMobile = useIsMobileDeliveryDevice();
    const [breadcrumbs, setBreadcrumbs] = useState([
        { id: albumPath, name: albumName || t("delivery.actions.defaultAlbumName") },
    ]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");
    const [errorCode, setErrorCode] = useState("");
    const [notFound, setNotFound] = useState(false);
    const [nextPageToken, setNextPageToken] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [viewMode, setViewMode] = useState("waterfall");
    const [actionSheet, setActionSheet] = useState(null);
    const [selectMode, setSelectMode] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState(() => new Map());
    const [selectedFolders, setSelectedFolders] = useState(() => new Map());
    const [downloadModal, setDownloadModal] = useState(null);
    const [downloadManifest, setDownloadManifest] = useState(null);
    const [downloadCount, setDownloadCount] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const [mobileZipPrompt, setMobileZipPrompt] = useState(null);
    const [mobileBatchSaveFiles, setMobileBatchSaveFiles] = useState(null);
    const [downloadAllMobileOpen, setDownloadAllMobileOpen] = useState(false);

    const currentFolder = breadcrumbs[breadcrumbs.length - 1];
    const pathPrefix = useMemo(() => getFolderPathPrefix(breadcrumbs), [breadcrumbs]);
    const imageItems = useMemo(
        () => items.filter((item) => isImageItem(item.mimeType)),
        [items]
    );

    const selectionSummary = useMemo(
        () => ({
            files: selectedFiles.size,
            folders: selectedFolders.size,
        }),
        [selectedFiles.size, selectedFolders.size]
    );

    const hasSelection = selectionSummary.files > 0 || selectionSummary.folders > 0;

    const closeActionSheet = useCallback(() => {
        setActionSheet(null);
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedFiles(new Map());
        setSelectedFolders(new Map());
    }, []);

    const exitSelectMode = useCallback(() => {
        setSelectMode(false);
        clearSelection();
    }, [clearSelection]);

    const buildSelectedFileEntry = useCallback(
        (file) => ({
            id: file.id,
            name: file.name,
            path: `${pathPrefix}${file.name}`,
            downloadUrl: file.downloadUrl,
        }),
        [pathPrefix]
    );

    const selectFile = useCallback(
        (file) => {
            if (isMobile && !isImageItem(file.mimeType)) {
                return;
            }

            setSelectedFiles((prev) => {
                if (prev.has(file.id)) {
                    return prev;
                }

                const next = new Map(prev);
                next.set(file.id, buildSelectedFileEntry(file));
                return next;
            });
        },
        [buildSelectedFileEntry, isMobile]
    );

    const deselectFile = useCallback((file) => {
        setSelectedFiles((prev) => {
            if (!prev.has(file.id)) {
                return prev;
            }
            const next = new Map(prev);
            next.delete(file.id);
            return next;
        });
    }, []);

    const toggleFileSelection = useCallback(
        (file) => {
            if (isMobile && !isImageItem(file.mimeType)) {
                return;
            }

            setSelectedFiles((prev) => {
                const next = new Map(prev);
                if (next.has(file.id)) {
                    next.delete(file.id);
                    return next;
                }

                next.set(file.id, buildSelectedFileEntry(file));
                return next;
            });
        },
        [buildSelectedFileEntry, isMobile]
    );

    const toggleFolderSelection = useCallback(
        (folder) => {
            if (isMobile) {
                return;
            }

            setSelectedFolders((prev) => {
                const next = new Map(prev);
                if (next.has(folder.id)) {
                    next.delete(folder.id);
                } else {
                    next.set(folder.id, {
                        id: folder.id,
                        name: folder.name,
                        pathPrefix: `${pathPrefix}${folder.name}/`,
                    });
                }
                return next;
            });
        },
        [isMobile, pathPrefix]
    );

    const selectAllVisible = useCallback(() => {
        if (isMobile) {
            setSelectedFiles(() => {
                const next = new Map();
                items.forEach((item) => {
                    if (!isImageItem(item.mimeType)) {
                        return;
                    }
                    next.set(item.id, buildSelectedFileEntry(item));
                });
                return next;
            });
            setSelectedFolders(new Map());
            return;
        }

        setSelectedFiles((prev) => {
            const next = new Map(prev);
            items.forEach((item) => {
                if (!isFolderItem(item.mimeType)) {
                    next.set(item.id, buildSelectedFileEntry(item));
                }
            });
            return next;
        });
        setSelectedFolders((prev) => {
            const next = new Map(prev);
            items.forEach((item) => {
                if (isFolderItem(item.mimeType)) {
                    next.set(item.id, {
                        id: item.id,
                        name: item.name,
                        pathPrefix: `${pathPrefix}${item.name}/`,
                    });
                }
            });
            return next;
        });
    }, [buildSelectedFileEntry, isMobile, items, pathPrefix]);

    const getItemById = useCallback(
        (itemId) => items.find((entry) => entry.id === itemId) || null,
        [items]
    );

    const dragSelect = useDragSelect({
        enabled: selectMode,
        isSelected: (itemId) => selectedFiles.has(itemId),
        onSelect: selectFile,
        onDeselect: deselectFile,
        getItemById,
    });

    const openMobileImageSave = useCallback(
        (files) => {
            closeActionSheet();
            setMobileBatchSaveFiles(files);
        },
        [closeActionSheet]
    );

    const downloadFileDirect = useCallback(
        async (file) => {
            const hide = message.loading(`${t("delivery.actions.downloadingPhotos")}…`, 0);
            try {
                await downloadDeliveryFile(file);
                closeActionSheet();
            } catch (err) {
                message.error(err.message || t("delivery.actions.downloadFailed"));
            } finally {
                hide();
            }
        },
        [closeActionSheet, t]
    );

    const handleDownloadFile = useCallback(
        async (file) => {
            if (isMobile && isImageItem(file.mimeType)) {
                openMobileImageSave([file]);
                return;
            }

            await downloadFileDirect(file);
        },
        [downloadFileDirect, isMobile, openMobileImageSave]
    );

    const handleLongPress = useCallback(
        (target) => {
            if (selectMode) {
                return;
            }

            if (target.type === "image" && target.item) {
                setSelectMode(true);
                selectFile(target.item);
                if (typeof navigator !== "undefined" && navigator.vibrate) {
                    navigator.vibrate(15);
                }
                return;
            }

            setActionSheet(target);
        },
        [selectFile, selectMode]
    );

    const { bind: bindLongPress, consumeIfTriggered } = useLongPress(handleLongPress);

    const mergeLongPressHandlers = useCallback(
        (data, onAction) => {
            const { onClick: longPressClick, ...restHandlers } = bindLongPress(data);
            return {
                ...restHandlers,
                onClick: (event) => {
                    longPressClick?.(event);
                    if (event.defaultPrevented) {
                        return;
                    }
                    onAction(event);
                },
            };
        },
        [bindLongPress]
    );

    const loadFolder = useCallback(
        async (folderId, { append = false, pageToken = null } = {}) => {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
                setError("");
                setErrorCode("");
                setNotFound(false);
            }

            try {
                const data = await listDeliveryFolder(toFolderPrefix(folderId), {
                    rootPrefix: albumPath,
                    pageToken,
                });

                setItems((prev) => (append ? [...prev, ...data.files] : data.files));
                setNextPageToken(data.nextPageToken || null);
            } catch (err) {
                if (!append) {
                    setItems([]);
                }
                if (err.code === "MISSING_API") {
                    setErrorCode("MISSING_API");
                    setError(err.message || t("delivery.actions.albumLoadFailed"));
                } else if (err.status === 404) {
                    // 404：相册确实不存在，不属于网络问题。
                    setNotFound(true);
                    setErrorCode("ALBUM_NOT_FOUND");
                    setError(t("delivery.actions.albumNotFound"));
                } else {
                    setErrorCode("ALBUM_LOAD_FAILED");
                    setError(t("delivery.actions.albumLoadFailed"));
                }
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [albumPath, t]
    );

    useEffect(() => {
        loadFolder(currentFolder.id);
    }, [currentFolder.id, loadFolder]);

    const handleOpenFolder = useCallback(
        (folder) => {
            if (selectMode && !isMobile) {
                toggleFolderSelection(folder);
                return;
            }
            if (consumeIfTriggered()) {
                return;
            }
            setBreadcrumbs((prev) => [
                ...prev,
                { id: folder.id, name: folder.name },
            ]);
        },
        [consumeIfTriggered, isMobile, selectMode, toggleFolderSelection]
    );

    const handleBackToParent = useCallback(() => {
        if (selectMode) {
            return;
        }
        setBreadcrumbs((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
    }, [selectMode]);

    const canGoUp = breadcrumbs.length > 1;

    const openActionSheetForFile = useCallback((file) => {
        setActionSheet({ type: "file", item: file });
    }, []);

    const openFilePreview = useCallback(
        (file) => {
            if (selectMode || consumeIfTriggered()) {
                return;
            }
            if (isDeliveryPreviewable(file)) {
                setPreviewFile(file);
                return;
            }
            openActionSheetForFile(file);
        },
        [consumeIfTriggered, openActionSheetForFile, selectMode]
    );

    const handleOpenFile = useCallback(
        (file) => {
            if (selectMode) {
                if (isMobile && !isImageItem(file.mimeType)) {
                    if (consumeIfTriggered()) {
                        return;
                    }
                    if (isDeliveryPreviewable(file)) {
                        setPreviewFile(file);
                        return;
                    }
                }
                toggleFileSelection(file);
                return;
            }

            openFilePreview(file);
        },
        [consumeIfTriggered, isMobile, openFilePreview, selectMode, toggleFileSelection]
    );

    const handleOpenImagePreview = useCallback(
        (file) => {
            openFilePreview(file);
        },
        [openFilePreview]
    );

    const handleImageSelectPointerUp = useCallback(
        (file, event) => {
            if (!selectMode) {
                return;
            }
            if (dragSelect.handlePointerUp(file, file.id, event)) {
                toggleFileSelection(file);
            }
        },
        [dragSelect, selectMode, toggleFileSelection]
    );

    const handleLoadMore = useCallback(() => {
        if (!nextPageToken || loadingMore) {
            return;
        }
        loadFolder(currentFolder.id, {
            append: true,
            pageToken: nextPageToken,
        });
    }, [currentFolder.id, loadFolder, loadingMore, nextPageToken]);

    const openDownloadConfirm = useCallback(
        async (payload) => {
            closeActionSheet();
            setDownloadCount(0);
            setDownloadManifest(null);
            setDownloadModal(payload);

            try {
                const manifest = await buildDownloadManifest(payload, { rootPrefix: albumPath });
                if (manifest.length === 0) {
                    setDownloadModal(null);
                    message.info(t("delivery.actions.noFilesToDownload"));
                    return;
                }
                setDownloadManifest(manifest);
                setDownloadCount(manifest.length);
            } catch {
                setDownloadModal(null);
                message.error(t("delivery.actions.countFilesFailed"));
            }
        },
        [closeActionSheet, t]
    );

    const requestZipDownload = useCallback(
        (payload) => {
            if (isMobile) {
                setMobileZipPrompt(payload);
                return;
            }
            openDownloadConfirm(payload);
        },
        [isMobile, openDownloadConfirm]
    );

    const openFolderDownloadConfirm = useCallback(
        (folder) => {
            requestZipDownload({
                type: "folder",
                zipName: folder.name,
                folders: [
                    {
                        id: folder.id,
                        name: folder.name,
                        pathPrefix: folder.pathPrefix ?? "",
                    },
                ],
            });
        },
        [requestZipDownload]
    );

    const handleMobileSelectionDownload = useCallback(() => {
        const files = Array.from(selectedFiles.values());

        if (files.length === 0) {
            return;
        }

        if (selectedFolders.size > 0) {
            requestZipDownload({
                type: "selection",
                zipName: currentFolder.name,
                files,
                folders: Array.from(selectedFolders.values()),
            });
            return;
        }

        setMobileBatchSaveFiles(files);
        exitSelectMode();
    }, [
        currentFolder.name,
        exitSelectMode,
        requestZipDownload,
        selectedFiles,
        selectedFolders,
    ]);

    const openSelectionDownloadConfirm = useCallback(() => {
        if (isMobile) {
            handleMobileSelectionDownload();
            return;
        }

        openDownloadConfirm({
            type: "selection",
            zipName: currentFolder.name,
            files: Array.from(selectedFiles.values()),
            folders: Array.from(selectedFolders.values()),
        });
    }, [
        currentFolder.name,
        handleMobileSelectionDownload,
        isMobile,
        openDownloadConfirm,
        selectedFiles,
        selectedFolders,
    ]);

    const handleConfirmDownload = useCallback(async () => {
        if (!downloadModal || downloading) {
            return;
        }

        setDownloading(true);
        let hide = message.loading(`${t("delivery.actions.downloading")}…`, 0);

        try {
            const progressHandler = ({ phase, current, total, failedCount }) => {
                hide();
                if (phase === "listing") {
                    hide = message.loading(t("delivery.actions.scanningFiles"), 0);
                } else if (phase === "fetching" || phase === "retrying") {
                    const failedHint =
                        failedCount > 0
                            ? t("delivery.actions.failedHint", { count: failedCount })
                            : "";
                    hide = message.loading(
                        t("delivery.actions.packingProgress", {
                            current,
                            total,
                            failedHint,
                        }),
                        0
                    );
                }
            };

            let result = null;

            if (downloadManifest?.length) {
                result = await downloadManifestAsStreamingZip(downloadManifest, {
                    zipName: downloadModal.zipName,
                    onProgress: progressHandler,
                });
            } else if (downloadModal.type === "folder") {
                result = await downloadFolderAsZip(
                    {
                        id: downloadModal.folders[0].id,
                        name: downloadModal.zipName,
                        pathPrefix: downloadModal.folders[0].pathPrefix,
                    },
                    { rootPrefix: albumPath, onProgress: progressHandler }
                );
            } else {
                result = await downloadEntriesAsStreamingZip({
                    files: downloadModal.files,
                    folders: downloadModal.folders,
                    zipName: downloadModal.zipName,
                    rootPrefix: albumPath,
                    onProgress: progressHandler,
                });
            }

            if (result?.failed?.length) {
                const failedCount = String(result.failed.length);
                message.warning(
                    t("delivery.actions.zipPartialSuccess", { failed: failedCount })
                );
            } else {
                message.success(t("delivery.actions.zipDownloadStarted"));
            }
            exitSelectMode();
        } catch (err) {
            message.error(err.message || t("delivery.actions.zipDownloadFailed"));
        } finally {
            hide();
            setDownloading(false);
            setDownloadModal(null);
            closeActionSheet();
        }
    }, [albumPath, closeActionSheet, downloadManifest, downloadModal, downloading, exitSelectMode, t]);

    const handleContinueMobileZip = useCallback(() => {
        if (!mobileZipPrompt) {
            return;
        }
        const payload = mobileZipPrompt;
        setMobileZipPrompt(null);
        openDownloadConfirm(payload);
    }, [mobileZipPrompt, openDownloadConfirm]);

    // “下载所有照片”：手机端弹出与站点一致的样式提示（文件较大建议用电脑）；
    // 电脑端按压缩包/谷歌云盘 ID 在新标签页打开。
    const handleDownloadAll = useCallback(() => {
        if (isMobile) {
            setDownloadAllMobileOpen(true);
            return;
        }

        const url = getDownloadAllUrl(downloadAll, albumPath);
        if (!url) {
            message.error(t("delivery.actions.downloadAllFailed"));
            return;
        }
        window.open(url, "_blank", "noopener,noreferrer");
    }, [albumPath, downloadAll, isMobile, t]);

    // 移动端弹窗里「仍然下载」：忽略提示，直接在新标签页打开下载地址。
    const handleDownloadAllAnyway = useCallback(() => {
        const url = getDownloadAllUrl(downloadAll, albumPath);
        if (!url) {
            message.error(t("delivery.actions.downloadAllFailed"));
            return;
        }
        window.open(url, "_blank", "noopener,noreferrer");
        setDownloadAllMobileOpen(false);
    }, [albumPath, downloadAll, t]);

    const folderMenuItems = [
        {
            key: "download-folder",
            icon: <DownloadOutlined />,
            label: <ActionLabel labelKey="selectFolderAll" />,
            onClick: () =>
                openFolderDownloadConfirm({
                    id: currentFolder.id,
                    name: currentFolder.name,
                    pathPrefix: "",
                }),
        },
    ];

    const renderSelectionCheckbox = (checked, selectable = true) => {
        if (!selectMode || !selectable) {
            return null;
        }

        return (
            <span className="delivery-browser__select" aria-hidden="true">
                <Checkbox checked={checked} tabIndex={-1} />
            </span>
        );
    };

    if (loading) {
        return (
            <div className="delivery-browser delivery-browser--centered">
                <Spin size="large" />
                <p>{t("delivery.actions.loadingAlbum")}</p>
            </div>
        );
    }

    if (error) {
        const showAlbumLoadFailed =
            errorCode === "ALBUM_LOAD_FAILED" ||
            errorCode === "MISSING_API" ||
            errorCode === "ALBUM_NOT_FOUND";

        return (
            <div className="delivery-browser delivery-browser--centered">
                {showAlbumLoadFailed ? (
                    <p className="delivery-album-error__text">
                        {notFound
                            ? t("delivery.actions.albumNotFound")
                            : t("delivery.actions.albumLoadFailed")}
                    </p>
                ) : (
                    <Text type="danger">{error}</Text>
                )}
                {errorCode === "MISSING_API" && !isDeliveryApiConfigured() ? (
                    <Text type="secondary" className="delivery-browser__hint">
                        {t("delivery.actions.missingApiHint")}
                    </Text>
                ) : notFound ? null : (
                    <Text type="secondary" className="delivery-browser__hint">
                        {t("common.networkTroubleshootHint")}
                    </Text>
                )}
                <div className="delivery-album-error__actions">
                    <Button type="primary" onClick={() => navigate("/contact")}>
                        <ActionLabel labelKey="contactPhotographer" />
                    </Button>
                    <Button onClick={() => loadFolder(currentFolder.id)}>
                        <ActionLabel labelKey="retry" />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className={`delivery-browser${selectMode ? " delivery-browser--selecting" : ""}`}>
            <div className="delivery-browser__toolbar">
                {canGoUp && (
                    <button
                        type="button"
                        className="delivery-browser__back"
                        onClick={handleBackToParent}
                        disabled={selectMode}
                        title={t("delivery.actions.backToParentTitle")}
                    >
                        <ArrowUpOutlined />
                        <span className="delivery-browser__back-label">
                            {t("delivery.actions.backToParent")}
                        </span>
                    </button>
                )}
                <span className="delivery-browser__current" title={currentFolder.name}>
                    {currentFolder.name}
                </span>
                <div className="delivery-browser__toolbar-actions">
                    {!selectMode && (
                        <DeliveryViewToggle mode={viewMode} onChange={setViewMode} />
                    )}
                    {selectMode && (
                        <Button type="default" size="small" onClick={selectAllVisible}>
                            <ActionLabel labelKey={isMobile ? "selectAllPhotos" : "selectAll"} />
                        </Button>
                    )}
                    {!selectMode && downloadAll && (
                        <Button
                            type="default"
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadAll}
                        >
                            <ActionLabel labelKey="downloadAllPhotos" />
                        </Button>
                    )}
                    <Button
                        type={selectMode ? "primary" : "default"}
                        size="small"
                        icon={selectMode ? <CheckOutlined /> : undefined}
                        onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                    >
                        <ActionLabel labelKey={selectMode ? "cancel" : "select"} />
                    </Button>
                    <Dropdown
                        menu={{ items: folderMenuItems }}
                        trigger={["click"]}
                        placement="bottomRight"
                    >
                        <button
                            type="button"
                            className="delivery-browser__toolbar-nub"
                            aria-label={t("delivery.actions.folderActions")}
                            title={t("delivery.actions.folderActions")}
                        >
                            <MoreOutlined />
                        </button>
                    </Dropdown>
                </div>
            </div>

            {items.length === 0 ? (
                <div className="delivery-browser--centered">
                    <Empty description={t("delivery.actions.emptyFolder")} />
                </div>
            ) : (
                <>
                    <div
                        className={`delivery-browser__grid${
                            viewMode === "waterfall" ? " delivery-browser__grid--waterfall" : ""
                        }`}
                    >
                        {items.map((item) => {
                                if (isFolderItem(item.mimeType)) {
                                    const checked = selectedFolders.has(item.id);
                                    const folderSelectable = !isMobile;
                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            className={`delivery-browser__item delivery-browser__item--folder${
                                                checked ? " delivery-browser__item--selected" : ""
                                            }`}
                                            {...(selectMode && folderSelectable
                                                ? { onClick: () => handleOpenFolder(item) }
                                                : mergeLongPressHandlers(
                                                      { type: "folder", item },
                                                      () => handleOpenFolder(item)
                                                  ))}
                                        >
                                            {renderSelectionCheckbox(checked, folderSelectable)}
                                            <FolderOutlined className="delivery-browser__folder-icon" />
                                            <span className="delivery-browser__name">{item.name}</span>
                                        </button>
                                    );
                                }

                                const thumbnail = getFileThumbnailUrl(item);
                                const checked = selectedFiles.has(item.id);
                                const fileSelectable = !isMobile || isImageItem(item.mimeType);

                                if (isImageItem(item.mimeType)) {
                                    const imageLongPress = selectMode
                                        ? {}
                                        : bindLongPress({
                                              type: "image",
                                              item,
                                          });

                                    return (
                                        <div
                                            key={item.id}
                                            data-delivery-select-id={item.id}
                                            className={`delivery-browser__item delivery-browser__item--image${
                                                checked ? " delivery-browser__item--selected" : ""
                                            }${selectMode ? " delivery-browser__item--selectable" : ""}`}
                                            onPointerDown={
                                                selectMode
                                                    ? (event) =>
                                                          dragSelect.handlePointerDown(
                                                              item,
                                                              item.id,
                                                              event
                                                          )
                                                    : undefined
                                            }
                                            onPointerMove={
                                                selectMode
                                                    ? (event) =>
                                                          dragSelect.handlePointerMove(
                                                              item,
                                                              item.id,
                                                              event
                                                          )
                                                    : undefined
                                            }
                                            onPointerEnter={
                                                selectMode
                                                    ? (event) =>
                                                          dragSelect.handlePointerEnter(
                                                              item,
                                                              item.id,
                                                              event
                                                          )
                                                    : undefined
                                            }
                                            onPointerUp={
                                                selectMode
                                                    ? (event) => handleImageSelectPointerUp(item, event)
                                                    : undefined
                                            }
                                        >
                                            {renderSelectionCheckbox(checked, fileSelectable)}
                                            {item.pinned && (
                                                <PushpinFilled
                                                    className="delivery-browser__pin-badge"
                                                    aria-label={t("delivery.actions.pinned")}
                                                    title={t("delivery.actions.pinned")}
                                                />
                                            )}
                                            <DeliveryLazyImage
                                                src={thumbnail}
                                                alt={item.name}
                                                layout={viewMode}
                                                className="delivery-browser__thumb"
                                                interactive={!selectMode}
                                                onClick={
                                                    selectMode
                                                        ? undefined
                                                        : () => handleOpenImagePreview(item)
                                                }
                                                longPressHandlers={imageLongPress}
                                            />
                                            {viewMode === "grid" && (
                                                <span className="delivery-browser__name">{item.name}</span>
                                            )}
                                        </div>
                                    );
                                }

                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        className={`delivery-browser__item delivery-browser__item--file${
                                            checked ? " delivery-browser__item--selected" : ""
                                        }`}
                                        {...(selectMode && fileSelectable
                                            ? { onClick: () => handleOpenFile(item) }
                                            : mergeLongPressHandlers(
                                                  { type: "file", item },
                                                  () => handleOpenFile(item)
                                              ))}
                                    >
                                        {renderSelectionCheckbox(checked, fileSelectable)}
                                        {thumbnail ? (
                                            <img
                                                src={thumbnail}
                                                alt=""
                                                className="delivery-browser__thumb"
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        ) : (
                                            renderFileIcon(item)
                                        )}
                                        <span className="delivery-browser__name">{item.name}</span>
                                    </button>
                                );
                            })}
                    </div>

                    {nextPageToken && (
                        <div className="delivery-browser__more">
                            <Button loading={loadingMore} onClick={handleLoadMore}>
                                {t("delivery.actions.loadMore")}
                            </Button>
                        </div>
                    )}

                    {!selectMode && (
                        <>
                            <FeedbackPrompt
                                sourceType={sourceType}
                                linkId={linkId}
                                albumPath={albumPath}
                                albumName={albumName || t("delivery.actions.defaultAlbumName")}
                            />
                            <div className="delivery-browser__social">
                                <SocialMediaCards />
                            </div>
                        </>
                    )}
                </>
            )}

            {selectMode && hasSelection && (
                <div className="delivery-browser__selection-bar">
                    <Text>
                        {t("delivery.actions.selectedCount")} {selectionSummary.files}{" "}
                        {isMobile
                            ? t("delivery.actions.selectedPhotosUnit")
                            : t("delivery.actions.selectedFilesUnit")}
                        {!isMobile && selectionSummary.folders > 0
                            ? t("delivery.actions.selectionFoldersPart", {
                                  folders: selectionSummary.folders,
                                  unit: t("delivery.actions.selectedFoldersUnit"),
                              })
                            : ""}
                    </Text>
                    <DownloadButton
                        type="primary"
                        labelKey={isMobile ? "downloadSelectedPhotos" : "downloadSelected"}
                        onClick={openSelectionDownloadConfirm}
                    />
                </div>
            )}

            {actionSheet && (
                <div
                    className="delivery-browser__action-sheet"
                    role="dialog"
                    aria-modal="true"
                    onClick={closeActionSheet}
                >
                    <div
                        className="delivery-browser__action-sheet-panel"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <p className="delivery-browser__action-sheet-title">{actionSheet.item.name}</p>
                        {actionSheet.type === "folder" ? (
                            <DownloadButton
                                block
                                labelKey="downloadAll"
                                onClick={() =>
                                    openFolderDownloadConfirm({
                                        id: actionSheet.item.id,
                                        name: actionSheet.item.name,
                                        pathPrefix: `${pathPrefix}${actionSheet.item.name}/`,
                                    })
                                }
                            />
                        ) : (
                            <DownloadButton
                                block
                                onClick={() => handleDownloadFile(actionSheet.item)}
                            />
                        )}
                        <Button block onClick={closeActionSheet}>
                            <ActionLabel labelKey="cancel" />
                        </Button>
                    </div>
                </div>
            )}

            <DeliveryFilePreview
                file={previewFile}
                open={Boolean(previewFile)}
                onClose={() => setPreviewFile(null)}
                onDownload={handleDownloadFile}
                files={imageItems}
                onNavigate={setPreviewFile}
            />

            <Modal
                open={Boolean(downloadModal)}
                title={
                    <>
                        <DownloadOutlined /> <ActionLabel labelKey="downloadAll" />
                    </>
                }
                onCancel={() => !downloading && setDownloadModal(null)}
                footer={[
                    <Button
                        key="cancel"
                        disabled={downloading}
                        onClick={() => setDownloadModal(null)}
                    >
                        {t("delivery.actions.cancel")}
                    </Button>,
                    <DownloadButton
                        key="download"
                        type="primary"
                        labelKey="downloadAll"
                        onClick={handleConfirmDownload}
                        disabled={downloadCount === 0}
                    />,
                ]}
            >
                {downloadCount > 0 ? (
                    <p>
                        {t("delivery.actions.downloadConfirm")} {downloadCount}{" "}
                        {t("delivery.actions.downloadConfirmSuffix")}
                    </p>
                ) : (
                    <p>{t("delivery.actions.countingFiles")}</p>
                )}
                <p className="delivery-browser__hint">{t("delivery.actions.downloadHint")}</p>
            </Modal>

            <Modal
                open={downloadAllMobileOpen}
                title={t("delivery.actions.downloadAllMobileTitle")}
                onCancel={() => setDownloadAllMobileOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setDownloadAllMobileOpen(false)}>
                        {t("delivery.actions.close")}
                    </Button>,
                    <Button key="continue" type="primary" danger onClick={handleDownloadAllAnyway}>
                        {t("delivery.actions.downloadAllMobileContinue")}
                    </Button>,
                ]}
                className="delivery-browser__modal"
                centered
                destroyOnClose
            >
                <p>{t("delivery.actions.downloadAllMobileBody")}</p>
            </Modal>

            <MobileZipSuggestModal
                open={Boolean(mobileZipPrompt)}
                onCancel={() => setMobileZipPrompt(null)}
                onContinueZip={handleContinueMobileZip}
            />

            <MobileBatchSavePanel
                files={mobileBatchSaveFiles}
                open={Boolean(mobileBatchSaveFiles?.length)}
                onClose={() => setMobileBatchSaveFiles(null)}
            />

            <ScrollNavigator />
        </div>
    );
}

export default DriveFolderBrowser;
